import { useState, useEffect, useCallback, useSyncExternalStore } from "react";
import { getVerifiedOnline, probeConnectivityNow, subscribeConnectivity } from "./connectivity";
import {
    buildReplayRequest,
    extractReplayErrorMessage,
    isAlreadyLanded,
    isPermanentFailure,
    MAX_REPLAY_ATTEMPTS,
    retryExhaustedMessage,
    type OfflineFileUpload,
} from "./offline-replay";

const DB_NAME = "ironclad-sync";
// v3 adds the `meta` store so the service worker can read the auth
// token during a Background Sync drain (the SW has no access to
// localStorage). Older clients with v2 (queue-only) auto-upgrade on
// next open via the additive onupgradeneeded below.
const DB_VERSION = 3;
const STORE_NAME = "queue";
const META_STORE = "meta";
const META_KEY_AUTH_TOKEN = "auth_token";
/** Sync tag the SW listens for; must match `sw.ts`. */
export const OFFLINE_SYNC_TAG = "drain-offline-queue";

/**
 * SELF-HEALING RETRY (#1505) — how long to wait before trying a queued save
 * again, escalating while it keeps failing and capped at two minutes.
 *
 * Why this exists: the drain used to run ONLY on an offline→online transition,
 * on mount, on focus, and on visibility-change. A save that queued while the
 * app already believed it was online — a weak-signal write that times out at
 * `SAVE_TIMEOUT_MS` — therefore never got another attempt for as long as the
 * tech stayed on the screen, and since submit is gated on the job's outbox the
 * job could not be closed at all. The field report: full bars, "1 save waiting
 * for signal", Submit Tests dead, the only escape a force-quit of the app.
 *
 * The floor is 15s so a real dead zone isn't hammered, and the drain still only
 * runs when we're verified-online, so retries in a dead zone cost nothing.
 */
const RETRY_DELAYS_MS = [15_000, 30_000, 60_000, 120_000] as const;

/**
 * OFFLINE-QUEUE HONESTY — a replay that ultimately failed. The entry is
 * PARKED in the queue (never deleted, payload preserved across reloads —
 * it lives in IndexedDB) and excluded from further automatic drains until
 * the tech explicitly retries it from the "N saves didn't sync" review
 * list. `terminal` marks a semantic 4xx the server will always reject —
 * only those may be discarded (with confirm) from the UI.
 */
export interface OutboxFailure {
    /** Epoch ms when the entry was parked. */
    at: number;
    /** HTTP status of the rejection, or null for a non-HTTP failure. */
    status: number | null;
    /** The server's error message (or a retry-exhaustion note). */
    message: string;
    /** True for semantic 4xx — retrying the identical payload can't succeed. */
    terminal: boolean;
}

interface OfflineAction {
    id?: number;
    url: string;
    method: string;
    body: unknown;
    /** When present, the action replays as multipart/form-data (e.g. photos). */
    fileUpload?: OfflineFileUpload;
    /**
     * #1512 — THE QUEUE DOES THE WAITING, NOT THE TECH.
     *
     * Set on a job submit. The drain will not replay this entry while ANY
     * earlier entry for the same job is still in the queue — pending or
     * parked. That is the guarantee the old submit button held by being
     * disabled: a submit must never land ahead of a reading it is supposed
     * to include. Moving it here keeps the guarantee and stops a tech
     * standing in a parking lot waiting for a queue to drain.
     */
    holdForJobId?: string;
    /** Replay attempts so far; used to park genuinely stuck entries. */
    attempts?: number;
    /** Set when the replay ultimately failed — parked, awaiting tech review. */
    failure?: OutboxFailure;
    createdAt: number;
}

/** Stores every reader/writer below assumes exist on an open connection. */
const REQUIRED_STORES = [STORE_NAME, META_STORE] as const;

function hasRequiredStores(db: IDBDatabase): boolean {
    return REQUIRED_STORES.every((name) => db.objectStoreNames.contains(name));
}

function rawOpenDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
            }
            // Create `meta` whenever it's missing rather than gating on
            // `oldVersion < 3`: an iOS connection can reach v3 without the
            // store actually present, and the old guard then never repaired
            // it on subsequent opens.
            if (!db.objectStoreNames.contains(META_STORE)) {
                db.createObjectStore(META_STORE, { keyPath: "key" });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(req.error ?? new Error("indexedDB open blocked"));
    });
}

function deleteDb(): Promise<void> {
    return new Promise((resolve) => {
        try {
            const req = indexedDB.deleteDatabase(DB_NAME);
            req.onsuccess = () => resolve();
            // A failed/blocked delete shouldn't wedge the caller; the
            // subsequent open will surface any real problem.
            req.onerror = () => resolve();
            req.onblocked = () => resolve();
        } catch {
            resolve();
        }
    });
}

/**
 * Open the offline DB, defending against a WebKit/iOS Safari bug where a
 * freshly opened connection reports `objectStoreNames` as empty even
 * though the DB already exists at the current version. When that happens
 * `onupgradeneeded` never fires, so the schema looks absent and any
 * `db.transaction("queue")` throws NotFoundError (DOMException code 8).
 *
 * Strategy: reopen a few times (the misreport is usually transient), and
 * only if the stores stay missing do we treat the DB as genuinely corrupt
 * and rebuild it. The queued actions in a store that can't be opened are
 * unreadable anyway, so recreating is the safe last resort rather than
 * crashing the field workflow.
 */
async function openDb(): Promise<IDBDatabase> {
    for (let attempt = 0; attempt < 3; attempt++) {
        const db = await rawOpenDb();
        if (hasRequiredStores(db)) return db;
        db.close();
        await new Promise<void>((r) => setTimeout(r, 50));
    }
    await deleteDb();
    const fresh = await rawOpenDb();
    if (!hasRequiredStores(fresh)) {
        throw new Error("offline-sync: object stores unavailable after recreate");
    }
    return fresh;
}

/**
 * Best-effort registration of a Background Sync tag. The browser will
 * fire `sync` in the SW when it next sees a healthy network — even if
 * the tab has been closed since the queue was written.
 *
 * Firefox / Safari don't implement the Background Sync API; this is a
 * progressive enhancement on top of the existing online-event drain
 * in `useOfflineSync`. Falls back silently.
 */
async function requestBackgroundSync(): Promise<void> {
    if (!("serviceWorker" in navigator)) return;
    try {
        const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
            sync?: { register: (tag: string) => Promise<void> };
        };
        if (reg.sync?.register) {
            await reg.sync.register(OFFLINE_SYNC_TAG);
        }
    } catch {
        // Permission denied / unsupported / SW not active — fine.
    }
}

/**
 * Mirror the active auth token into IndexedDB so the SW can replay
 * queued requests with the correct Authorization header during a
 * Background Sync drain. Called from `api.setToken` / `api.clearToken`
 * on every token change. Failures here must not block auth.
 */
export async function mirrorAuthTokenForSw(token: string | null): Promise<void> {
    try {
        const db = await openDb();
        const tx = db.transaction(META_STORE, "readwrite");
        const store = tx.objectStore(META_STORE);
        if (token) {
            store.put({ key: META_KEY_AUTH_TOKEN, value: token });
        } else {
            store.delete(META_KEY_AUTH_TOKEN);
        }
        await new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch {
        // IDB unavailable (e.g. private window on some browsers) — no-op.
    }
}

/** Order-independent serialization of a JSON body, so re-taps whose object
 *  keys were built in a different order still compare equal. */
function stableStringify(value: unknown): string {
    if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj)
        .sort()
        .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
        .join(",")}}`;
}

/** Identity of a queued action for dedupe: same method + url + (order
 *  -independent) body ⇒ same key. Exported for unit tests. */
export function actionDedupeKey(a: { method: string; url: string; body: unknown }): string {
    return `${a.method} ${a.url} ${stableStringify(a.body)}`;
}

/** An eligible (non-parked, non-upload) queued action with the identical
 *  method + url + body already waiting to replay. Returns its id, or null. */
async function findDuplicateActionId(
    db: IDBDatabase,
    action: { url: string; method: string; body: unknown },
): Promise<number | null> {
    const key = actionDedupeKey(action);
    for (const a of await readAllActions(db)) {
        if (a.failure || a.fileUpload || a.id == null) continue;
        if (actionDedupeKey(a) === key) return a.id;
    }
    return null;
}

export async function queueOfflineAction(action: {
    url: string;
    method: string;
    body: unknown;
    fileUpload?: OfflineFileUpload;
    /** #1512 — hold this entry until the rest of the job's queue has landed. */
    holdForJobId?: string;
}): Promise<number> {
    return (await queueOfflineActionDetailed(action)).id;
}

/**
 * Same as `queueOfflineAction`, but also says whether the save was folded
 * into an entry that was ALREADY waiting (dedupe). The write-through save
 * needs to know: an entry that was already queued holds readings from an
 * earlier tap (often one made with no signal), so a rejection must park it
 * in "Failed saves" rather than delete it.
 */
export async function queueOfflineActionDetailed(action: {
    url: string;
    method: string;
    body: unknown;
    fileUpload?: OfflineFileUpload;
    holdForJobId?: string;
}): Promise<{ id: number; reused: boolean }> {
    const db = await openDb();
    // Dedupe: a tech re-tapping a save that silently queued — or the same
    // mutation firing twice — must not stack identical rows. Each duplicate
    // would replay on reconnect and re-drive the same server action (submit,
    // On-My-Way, disposition), which is exactly the "double work" report. If
    // an eligible entry with the identical payload is already queued, reuse
    // it. File uploads are NEVER deduped: losing a field photo is worse than a
    // rare duplicate, and their bodies aren't a reliable identity key.
    if (!action.fileUpload) {
        const existingId = await findDuplicateActionId(db, action);
        if (existingId != null) {
            void requestBackgroundSync();
            return { id: existingId, reused: true };
        }
    }
    const id = await new Promise<number>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const req = tx.objectStore(STORE_NAME).add({
            ...action,
            createdAt: Date.now(),
        } satisfies Omit<OfflineAction, "id">);
        req.onsuccess = () => resolve(req.result as number);
        tx.onerror = () => reject(tx.error);
    });
    // Fire-and-forget; do not block the caller on registration.
    void requestBackgroundSync();
    return { id, reused: false };
}

/**
 * Park one queued entry in "Failed saves" with a plain reason — the same
 * state the drain leaves a server-rejected replay in. Used by the
 * write-through save when a rejection lands on an entry that was already
 * waiting. Never deletes: the payload stays on the device.
 */
export async function parkQueuedAction(
    id: number,
    failure: { status: number | null; message: string; terminal: boolean },
): Promise<void> {
    try {
        const db = await openDb();
        const entry = await new Promise<OfflineAction | undefined>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, "readonly");
            const req = tx.objectStore(STORE_NAME).get(id);
            req.onsuccess = () => resolve(req.result as OfflineAction | undefined);
            req.onerror = () => reject(req.error);
        });
        if (!entry) return;
        await markActionFailed(db, entry, failure);
    } catch {
        // IDB trouble: the entry stays queued and the drain parks it itself.
    }
}

/** Remove a single queued action (e.g. after its write-through attempt
 *  succeeded — TECH-JOB-SCREEN §8's outbox-first save). */
export async function removeQueuedAction(id: number): Promise<void> {
    try {
        const db = await openDb();
        await deleteAction(db, id);
    } catch {
        // The drain will replay it; the server upsert is idempotent.
    }
}

export interface QueuedActionSummary {
    id: number;
    url: string;
    method: string;
    createdAt: number;
    attempts: number;
}

/** A queued action belongs to a job when its URL or JSON body carries the job
 *  id. One definition, used by the job-scoped pill AND by the #1512 submit
 *  hold — those two must never disagree about what "this job's work" means. */
function actionBelongsToJob(a: { url: string; body: unknown }, jobId: string): boolean {
    if (a.url.includes(jobId)) return true;
    const body = a.body as { jobId?: unknown } | null;
    return !!body && typeof body === "object" && body.jobId === jobId;
}

/** TECH-JOB-SCREEN §8 — the workflow's "N saves waiting for signal" pill
 *  needs the outbox scoped to ONE job. */
export async function getPendingActionsForJob(jobId: string): Promise<QueuedActionSummary[]> {
    try {
        const db = await openDb();
        const all = await new Promise<OfflineAction[]>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, "readonly");
            const req = tx.objectStore(STORE_NAME).getAll();
            req.onsuccess = () => resolve(req.result as OfflineAction[]);
            req.onerror = () => reject(req.error);
        });
        return all
            .filter((a) => !a.failure)
            .filter((a) => actionBelongsToJob(a, jobId))
            .map((a) => ({
                id: a.id!,
                url: a.url,
                method: a.method,
                createdAt: a.createdAt,
                attempts: a.attempts ?? 0,
            }));
    } catch {
        return [];
    }
}

/**
 * #1512 — the jobs whose SUBMIT is sitting in the queue, so the tech's screens
 * can say "Sending to the office" instead of leaving him wondering whether the
 * tap took. Parked submits are included: from the tech's point of view it has
 * still not reached the office, and the red "didn't sync" chip carries the
 * detail.
 */
export async function getQueuedSubmitJobIds(): Promise<string[]> {
    try {
        const db = await openDb();
        const all = await readAllActions(db);
        const ids = new Set<string>();
        for (const a of all) if (a.holdForJobId) ids.add(a.holdForJobId);
        return [...ids];
    } catch {
        return [];
    }
}

function readAllActions(db: IDBDatabase): Promise<OfflineAction[]> {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).getAll();
        req.onsuccess = () => resolve(req.result as OfflineAction[]);
        req.onerror = () => reject(req.error);
    });
}

/** Pending = queued and still eligible for automatic replay. Parked
 *  failures are NOT pending — they surface through getFailedActions and
 *  the "N saves didn't sync" chip instead of the waiting-for-signal pill. */
export async function getPendingCount(): Promise<number> {
    try {
        const db = await openDb();
        const all = await readAllActions(db);
        return all.filter((a) => !a.failure).length;
    } catch {
        return 0;
    }
}

export interface FailedActionSummary {
    id: number;
    url: string;
    method: string;
    body: unknown;
    createdAt: number;
    attempts: number;
    failure: OutboxFailure;
}

/** Parked failures, oldest first — feeds the failed-saves review list. */
export async function getFailedActions(): Promise<FailedActionSummary[]> {
    try {
        const db = await openDb();
        const all = await readAllActions(db);
        return all
            .filter((a): a is OfflineAction & { failure: OutboxFailure } => !!a.failure && a.id != null)
            .sort((a, b) => a.createdAt - b.createdAt)
            .map((a) => ({
                id: a.id!,
                url: a.url,
                method: a.method,
                body: a.body,
                createdAt: a.createdAt,
                attempts: a.attempts ?? 0,
                failure: a.failure,
            }));
    } catch {
        return [];
    }
}

export async function getFailedCount(): Promise<number> {
    return (await getFailedActions()).length;
}

/**
 * Un-park a failed entry: clear the failure + attempt counter so the next
 * drain (which the caller should trigger) replays it fresh. Returns false
 * when the entry no longer exists.
 */
export async function retryFailedAction(id: number): Promise<boolean> {
    const db = await openDb();
    const entry = await new Promise<OfflineAction | undefined>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(id);
        req.onsuccess = () => resolve(req.result as OfflineAction | undefined);
        req.onerror = () => reject(req.error);
    });
    if (!entry) return false;
    const { failure: _failure, ...rest } = entry;
    await saveAction(db, { ...rest, attempts: 0 });
    return true;
}

/**
 * Discard a parked failure — allowed ONLY for terminal (semantic 4xx)
 * rejections; a retryable failure must never be destroyable from the UI.
 * Returns true when the entry was deleted.
 */
export async function discardFailedAction(id: number): Promise<boolean> {
    const db = await openDb();
    const entry = await new Promise<OfflineAction | undefined>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(id);
        req.onsuccess = () => resolve(req.result as OfflineAction | undefined);
        req.onerror = () => reject(req.error);
    });
    if (!entry || !entry.failure?.terminal) return false;
    await deleteAction(db, id);
    return true;
}

function deleteAction(db: IDBDatabase, id: number): Promise<void> {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).delete(id);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

function saveAction(db: IDBDatabase, action: OfflineAction): Promise<void> {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).put(action);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

/**
 * Drain the offline queue.
 *
 * OFFLINE-QUEUE HONESTY: nothing is EVER deleted on failure. A semantic
 * 4xx (isPermanentFailure) parks the entry with the server's error message
 * (`failure.terminal = true`); a retryable failure that exhausts
 * MAX_REPLAY_ATTEMPTS parks it retryable (`terminal = false`). Parked
 * entries are skipped by subsequent drains and surface in the field UI as
 * the red "N saves didn't sync — tap to review" chip, which offers retry
 * (both kinds) and discard-with-confirm (terminal only). The payload
 * persists in IndexedDB across reloads either way.
 *
 * (Historical failure mode this replaces: the drain deleted any permanent
 * rejection with only a console.warn — offline photos queued as JSON
 * against the multipart-only endpoint 400'd and vanished silently.)
 */
export async function syncPendingActions(
    opts: { countAttempts?: boolean } = {},
): Promise<{ synced: number; failed: number; held: number }> {
    // #1505 — a drain the tech FORCED while we still read offline must not
    // burn replay attempts: eight futile tries would park the entry as
    // "didn't sync", which drops it out of the job's outbox count and
    // silently unblocks submit around an unsent reading. A forced attempt
    // either lands or changes nothing. (A semantic 4xx still parks either
    // way — that is a server verdict, not a connectivity one.)
    const countAttempts = opts.countAttempts ?? true;
    const db = await openDb();
    const all = await readAllActions(db);

    let synced = 0;
    let failed = 0;
    let held = 0;

    // #1512 — live view of what is still in the queue, so a held submit is
    // judged against reality rather than the snapshot we started with: the
    // readings ahead of it are replayed earlier in THIS loop, and clearing
    // them must release the submit on the same pass, not the next one.
    // Parked entries deliberately stay in this set — a reading that failed
    // for good must hold its own job's submit rather than let the job file
    // around it.
    const stillQueued = new Map<number, OfflineAction>();
    for (const a of all) if (a.id != null) stillQueued.set(a.id, a);

    for (const action of all) {
        // Parked failures wait for an explicit retry from the review list.
        if (action.failure) continue;
        // A submit waits for its own job's work to land first.
        if (action.holdForJobId && action.id != null) {
            const blocker = [...stillQueued.values()].some(
                (other) =>
                    other.id != null &&
                    other.id !== action.id &&
                    other.id < action.id! &&
                    actionBelongsToJob(other, action.holdForJobId!),
            );
            if (blocker) {
                held++;
                continue;
            }
        }
        try {
            const { body, headers } = buildReplayRequest(action);
            const replayHeaders: Record<string, string> = { ...headers };
            const token = localStorage.getItem("bf_token");
            if (token) replayHeaders["Authorization"] = `Bearer ${token}`;

            const res = await fetch(action.url, {
                method: action.method,
                headers: replayHeaders,
                body,
            });

            if (res.ok) {
                await deleteAction(db, action.id!);
                stillQueued.delete(action.id!);
                synced++;
            } else {
                // Read the body once — res.text() can only be consumed once,
                // and both the already-landed check and the parked-failure
                // message need it.
                const bodyText = await res.text().catch(() => "");
                if (isAlreadyLanded(res.status, bodyText)) {
                    // The write already landed server-side (a timed-out attempt
                    // that actually succeeded, or a duplicate re-tap). Clearing
                    // it as a success mirrors the online path's silent
                    // ALREADY_RECORDED accept — not a "save didn't sync".
                    await deleteAction(db, action.id!);
                    stillQueued.delete(action.id!);
                    synced++;
                } else if (isPermanentFailure(res.status)) {
                    // Semantic rejection — retrying the same payload can't help.
                    // Park it with the server's message so the tech SEES it.
                    const message = extractReplayErrorMessage(bodyText, res.status);
                    console.warn(`[offline-sync] parking ${action.method} ${action.url}: permanent ${res.status}`);
                    await markActionFailed(db, action, { status: res.status, message, terminal: true });
                    failed++;
                } else {
                    await bumpAttemptsOrPark(db, action, res.status, `status ${res.status}`, countAttempts);
                    failed++;
                }
            }
        } catch (err) {
            await bumpAttemptsOrPark(
                db,
                action,
                null,
                err instanceof Error ? err.message : "network error",
                countAttempts,
            );
            failed++;
        }
    }

    return { synced, failed, held };
}

async function markActionFailed(
    db: IDBDatabase,
    action: OfflineAction,
    failure: { status: number | null; message: string; terminal: boolean },
): Promise<void> {
    await saveAction(db, {
        ...action,
        attempts: (action.attempts ?? 0) + 1,
        failure: { at: Date.now(), ...failure },
    });
}

/** Increment an action's attempt counter, parking it (retryable, never
 *  deleted) once it's clearly stuck. */
async function bumpAttemptsOrPark(
    db: IDBDatabase,
    action: OfflineAction,
    status: number | null,
    reason: string,
    countAttempts = true,
): Promise<void> {
    // A free attempt (see `syncPendingActions`): leave the entry exactly as it
    // was so it stays eligible and keeps its place in the outbox count.
    if (!countAttempts) return;
    const attempts = (action.attempts ?? 0) + 1;
    if (attempts >= MAX_REPLAY_ATTEMPTS) {
        console.warn(`[offline-sync] parking ${action.method} ${action.url} after ${attempts} attempts (${reason})`);
        await saveAction(db, {
            ...action,
            attempts,
            failure: {
                at: Date.now(),
                status,
                message: retryExhaustedMessage(attempts),
                terminal: false,
            },
        });
        return;
    }
    await saveAction(db, { ...action, attempts });
}

/**
 * Drain guard and retry cadence live at MODULE scope, not per-hook.
 * `useOfflineSync` is mounted by several components at once (the sync banner,
 * the field index, and three places inside the backflow workflow), so a
 * per-instance guard let one focus event start five simultaneous drains of the
 * same queue. One shared guard collapses them into a single pass.
 */
let drainInFlight = false;
/** How many consecutive retry waits have elapsed for the current backlog —
 *  indexes `RETRY_DELAYS_MS`. Reset when the queue empties. */
let retryStep = 0;

/** Test-only: clear the module-scoped drain state between cases. */
export function __resetOfflineSyncForTests(): void {
    drainInFlight = false;
    retryStep = 0;
}

/**
 * #1512 — "is this job's submit still on the phone?", polled on the same 5s
 * cadence as the outbox count so a screen can show it and clear it without the
 * tech doing anything.
 */
export function useQueuedSubmitJobIds(): string[] {
    const [ids, setIds] = useState<string[]>([]);
    useEffect(() => {
        let cancelled = false;
        const read = () => {
            void getQueuedSubmitJobIds().then((next) => {
                if (!cancelled) setIds((prev) => (sameIds(prev, next) ? prev : next));
            });
        };
        read();
        const id = setInterval(read, 5_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);
    return ids;
}

/** Keep the hook's identity stable when nothing changed, so a screen doesn't
 *  re-render every 5 seconds for a list that is usually empty. */
function sameIds(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function useOfflineSync() {
    // Verified connectivity, NOT the raw `navigator.onLine` flag: the flag is
    // a false-negative that flips to "offline" with full signal, and this hook
    // gates every field save (`if (!isOnline) queueOfflineAction`). See
    // `connectivity.ts` for the reachability-confirmed state machine.
    const isOnline = useSyncExternalStore(subscribeConnectivity, getVerifiedOnline);
    const [pendingCount, setPendingCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    // NOTE: the in-flight guard is the module-scoped `drainInFlight` above, and
    // it must never enter `sync`'s dependency array. With `syncing` as a dep,
    // every setSyncing toggle recreated `sync`, which re-ran the mount/focus
    // drain effect below, which called sync() again — an endless ~100ms loop
    // that strobed the SyncBanner (the "Syncing 0 pending items…" flicker
    // shoving the page up and down).

    const refreshCount = useCallback(() => {
        getPendingCount().then(setPendingCount);
        getFailedCount().then(setFailedCount);
    }, []);

    useEffect(() => {
        refreshCount();
        const id = setInterval(refreshCount, 5_000);
        // The SW fires this after a Background Sync drain finishes
        // (success or partial). Refreshing immediately drops the
        // pending-count badge to 0 without waiting for the next poll
        // tick, which would feel laggy to the tech.
        const swMessageHandler = (event: MessageEvent) => {
            const data = event.data as { type?: string } | null;
            if (data?.type === "OFFLINE_QUEUE_DRAINED") refreshCount();
        };
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener("message", swMessageHandler);
        }
        return () => {
            clearInterval(id);
            if ("serviceWorker" in navigator) {
                navigator.serviceWorker.removeEventListener("message", swMessageHandler);
            }
        };
    }, [refreshCount]);

    /**
     * Drain the queue.
     *
     * `force` is the tech tapping "Retry now" (#1505). It re-probes
     * connectivity first — the browser's offline flag is a known false-negative
     * — and then attempts the send even if that probe still says offline,
     * because an explicit tap outranks our guess. A forced attempt made while
     * we read offline does NOT count toward parking the entry.
     */
    const sync = useCallback(
        async (opts: { force?: boolean } = {}) => {
            if (drainInFlight) return { synced: 0, failed: 0, held: 0 };
            let online = getVerifiedOnline();
            if (!online && opts.force) online = await probeConnectivityNow();
            if (!online && !opts.force) return { synced: 0, failed: 0, held: 0 };
            drainInFlight = true;
            try {
                // An empty queue needs no visible "Syncing…" phase — just refresh
                // the badge and bail. Without this, every drain trigger (mount,
                // focus, visibility) flashed the banner for a zero-item sync.
                const pending = await getPendingCount();
                setPendingCount(pending);
                if (pending === 0) return { synced: 0, failed: 0, held: 0 };

                setSyncing(true);
                const result = await syncPendingActions({ countAttempts: online });
                refreshCount();
                return result;
            } catch (err) {
                // The drain is fired-and-forgotten from several effects via
                // `void sync()`, so any throw here (e.g. a transient IndexedDB
                // failure) would otherwise bubble up as an unhandled rejection.
                console.warn("[offline-sync] drain failed", err);
                return { synced: 0, failed: 0, held: 0 };
            } finally {
                drainInFlight = false;
                setSyncing(false);
            }
        },
        [refreshCount],
    );

    // Signal came back — drain immediately.
    //
    // This deliberately does NOT gate on `pendingCount`. That state is only
    // polled every 5s, so it could still read a stale 0 at the instant
    // connectivity flipped; the drain was then skipped and never re-armed until
    // the app was backgrounded and reopened (#1505). `sync()` reads the real
    // count from IndexedDB itself and bails on an empty queue, so calling it
    // unconditionally here is both correct and cheap.
    useEffect(() => {
        if (isOnline) void sync();
    }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

    // iOS Safari does not reliably fire the `online` event after a cold
    // reopen of a home-screen PWA, so the online-only drain above can miss
    // a reconnect. Also drain on initial mount, when the window regains
    // focus, and when the tab becomes visible again — each is a strong
    // signal the tech is back and likely back online.
    useEffect(() => {
        const drain = () => {
            if (getVerifiedOnline()) void sync();
        };
        const onVisibility = () => {
            if (document.visibilityState === "visible") drain();
        };
        drain();
        window.addEventListener("focus", drain);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("focus", drain);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [sync]);

    // #1505 — SELF-HEALING RETRY. Everything above drains on an EVENT (a
    // connectivity flip, a focus, a reopen). A save that queued while the app
    // already believed it was online produces no such event, so it used to sit
    // in the outbox forever with the tech watching a disabled Submit button.
    // While anything is queued, keep retrying on an escalating backoff until
    // the queue clears — no tech action, no app restart.
    useEffect(() => {
        if (pendingCount === 0) {
            retryStep = 0;
            return;
        }
        const delay = RETRY_DELAYS_MS[Math.min(retryStep, RETRY_DELAYS_MS.length - 1)];
        const id = setTimeout(() => {
            retryStep += 1;
            // Verified-online only: a retry in a genuine dead zone can't land,
            // and attempting it would burn the entry's replay attempts toward
            // being parked as failed. `connectivity.ts` re-probes on its own
            // cadence, and the effect above drains the moment it recovers.
            if (getVerifiedOnline()) void sync();
        }, delay);
        return () => clearTimeout(id);
    }, [pendingCount, syncing, isOnline, sync]);

    return { pendingCount, failedCount, sync, isOnline, syncing, refreshCount };
}
